import { defineStore } from 'pinia'
import { useI18n } from 'vue-i18n'
import debounce from 'lodash/debounce'
import xor from 'lodash/xor'
import { toDay, toHour } from '~/assets/date'

// Provide a single debounce that we can wrap around all fetch calls
const fetchDebounce = debounce((call: () => void) => call(), 300)

/**
 * Series overview state.
 */
export const useSeriesOverviewStore = defineStore('series-overview', () => {
  const cacheStore = useCacheStore()
  const pageStore = usePageStore()
  const cmsFetch = useCmsFetch()
  const graphqlFetch = useGraphqlFetch()
  const { locale } = useI18n()

  // Global results (i.e. unaffected by search context)
  const allFacets = ref<any>({})

  // Search context
  const date = ref(toDay('today').toISOString())
  const genres = ref<string[]>([])
  const keyword = ref('')
  const organizers = ref<string[]>([])
  const page = ref(1)
  const rooms = ref<string[]>([])

  // Highlighted organizer (only set when a single organizer facet is active)
  const highlightedOrganizer = ref<any>(undefined)

  // Banner results
  const fallbackBanners = ref<any[]>([])
  const banners = ref<any[]>([])

  // Search results
  const series = ref<any[]>([])
  /*
   * Raw per-search facets from Elasticsearch, named differently from the `facets` getter that exposes
   * it - Pinia shares one namespace for state and getters, so they can't share a name.
   */
  const rawFacets = ref<any>({})
  const hits = ref<string[]>([])
  const loading = ref(true)
  const totalHits = ref(0)

  // Constants
  const hitsPerPage = computed(() => 15)

  // Url parameters
  const urlParameters = computed(() => ({
    genres: genres.value,
    keyword: keyword.value || undefined,
    organizers: organizers.value,
    page: page.value === 1 ? undefined : page.value,
    rooms: rooms.value,
  }))

  // Banner results
  const bannersAtPosition = computed(() => {
    return {
      // First the fallback banners
      ...Object.fromEntries(fallbackBanners.value.map((banner) => [banner.seriesPosition, banner])),
      // Second the banners based on search criteria, these will overwrite the fallback banners
      ...Object.fromEntries(banners.value.map((banner) => [banner.seriesPosition, banner])),
    }
  })

  // Search results
  const facets = computed(() => {
    const helper = (fromAllFacets: any[], fromFacets: any[], activeList: string[]) => {
      const currentCountMap = Object.fromEntries((fromFacets || []).map(({ count, label, value }) => [value || label, count]))
      return (fromAllFacets || []).map(({ value, label }) => ({
        active: activeList.includes(value || label),
        count: currentCountMap[value || label] || 0,
        label,
        value: value || label,
      }))
    }
    return [
      { key: 'organizers', values: helper(allFacets.value.organizers, rawFacets.value.organizers, organizers.value) },
      { key: 'genres', values: helper(allFacets.value.genres, rawFacets.value.genres, genres.value) },
      { key: 'rooms', values: helper(allFacets.value.rooms, rawFacets.value.rooms, rooms.value) },
    ]
  })
  const activeFacets = computed(() => {
    const helper = (fromAllFacets: any[], activeList: string[], facet: string) =>
      (fromAllFacets || []).filter(({ value, label }) => activeList.includes(value || label)).map(({ value, label }) => ({ facet, label, value: value || label }))
    return [
      ...helper(allFacets.value.genres, genres.value, 'genres'),
      ...helper(allFacets.value.organizers, organizers.value, 'organizers'),
      ...helper(allFacets.value.rooms, rooms.value, 'rooms'),
    ]
  })

  // Global results (i.e. unaffected by search context)
  function setAllFacets(newAllFacets: any) {
    allFacets.value = newAllFacets
  }

  // Initialize
  function initialize({
    genres: gnr,
    organizers: org,
    page: pg,
    rooms: rms,
    keyword: kw,
    // 'organizers' and 'rooms' were previously available using different parameters.
    // We take this into account for backwards compatability.
    organizer: backwardsCompatibleOrganizers,
    room: backwardCompatibleRooms,
  }: {
    genres?: any
    organizers?: any
    page?: any
    rooms?: any
    keyword?: any
    organizer?: any
    room?: any
  }) {
    // When there are no values given, i.e. from the URL, we will simply keep the most recently used search context
    if (
      gnr === undefined &&
      org === undefined &&
      pg === undefined &&
      rms === undefined &&
      kw === undefined &&
      backwardsCompatibleOrganizers === undefined &&
      backwardCompatibleRooms === undefined
    ) {
      return
    }

    const toArray = (value: any) => (value === undefined ? [] : Array.isArray(value) ? value : [value])
    const toBackwardCompatibleArray = (value: any, backwardCompatibleValue: any) => (value === undefined ? toArray(backwardCompatibleValue) : toArray(value))
    genres.value = toArray(gnr)
    keyword.value = typeof kw === 'string' ? kw : ''
    organizers.value = toBackwardCompatibleArray(org, backwardsCompatibleOrganizers)
    page.value = typeof pg === 'string' ? Math.max(1, Number.parseInt(pg)) : 1
    rooms.value = toBackwardCompatibleArray(rms, backwardCompatibleRooms)
  }

  // Highlighted organizer (only set when a single organizer facet is active)
  function setHighlightedOrganizer(newHighlightedOrganizer: any) {
    highlightedOrganizer.value = newHighlightedOrganizer
  }

  // Search context
  /*
   * Applies a route query back onto the search context, so browser back/forward restores the overview.
   * Mirrors `urlParameters` above, which produces the query in the first place. This action was missing
   * (SeriesOverview.vue has been calling it since the Vuex days, and throwing) - see that component.
   */
  function setUrlParameters(query: any) {
    const toArray = (value: any) => (value === undefined ? [] : Array.isArray(value) ? value : [value])
    const toValue = (value: any, fallback: any) => (value === undefined ? fallback : Array.isArray(value) ? value?.[0] : value)
    genres.value = toArray(query.genres)
    keyword.value = toValue(query.keyword, '')
    organizers.value = toArray(query.organizers)
    page.value = Math.max(1, toValue(query.page, 1))
    rooms.value = toArray(query.rooms)
    fetchHighlightedOrganizer()
    fetchSeries()
    fetchBanners()
  }
  function reset() {
    genres.value = []
    keyword.value = ''
    organizers.value = []
    page.value = 1
    rooms.value = []
    fetchHighlightedOrganizer()
    fetchSeries()
    fetchBanners()
  }
  function setKeyword(newKeyword: string) {
    keyword.value = newKeyword
    page.value = 1
    // Debounce because we do not want to fetch on every keystroke
    fetchDebounce(() => fetchSeries())
  }
  function setPage(newPage: number) {
    page.value = Math.max(1, newPage)
    fetchSeries()
  }
  function toggleFacet({ name, value }: { name: string; value: string }) {
    switch (name) {
      case 'genres':
        genres.value = xor(genres.value, [value])
        page.value = 1
        fetchSeries()
        fetchBanners()
        break
      case 'organizers':
        organizers.value = xor(organizers.value, [value])
        page.value = 1
        fetchHighlightedOrganizer()
        fetchSeries()
        fetchBanners()
        break
      case 'rooms':
        rooms.value = xor(rooms.value, [value])
        page.value = 1
        fetchSeries()
        fetchBanners()
        break
      default:
        console.error(`Unable to toggle unknown facet "${name}" with value "${value}"`)
        break
    }
  }

  // Banner results
  function setFallbackBanners(newFallbackBanners: any[]) {
    fallbackBanners.value = newFallbackBanners
  }
  function setBanners(newBanners: any[]) {
    banners.value = newBanners
  }

  // Search results
  function setFacets(newFacets: any) {
    rawFacets.value = newFacets
  }
  function setHits(newHits: string[]) {
    hits.value = newHits
  }
  function setLoading(newLoading: boolean) {
    loading.value = newLoading
  }
  function setSeries(newSeries: any[]) {
    series.value = newSeries
  }
  function setTotalHits(newTotalHits: number) {
    totalHits.value = newTotalHits
  }

  async function fetchFallbackBanners() {
    const key = `series-fallback-banners;${locale.value}`
    const ttl = 3600

    const fetcher = async () => {
      return (await cmsFetch(`/api/${locale.value}/series/fallback-banners.json`)).data
    }

    const fetchedFallbackBanners = await cacheStore.fetch({ key, ttl, fetcher, fallback: [] })
    setFallbackBanners(fetchedFallbackBanners)
    return fetchedFallbackBanners
  }

  async function fetchBanners() {
    const key = `series-banners;${locale.value};organizers:${organizers.value.join(',')};genres:${genres.value.join(',')};rooms:${rooms.value.join(',')}`
    const ttl = 3600

    // We only support showing special banners when only one of the facets is used
    let fetchedBanners: any[] = []
    const sections = [organizers.value.length > 0 ? 'organizer' : undefined, genres.value.length > 0 ? 'genre' : undefined, rooms.value.length > 0 ? 'room' : undefined].filter(
      (section) => section,
    )

    if (sections.length === 1) {
      const fetcher = async () => {
        return (
          await cmsFetch(
            `/api/${locale.value}/banners/series/${sections[0]}/${[...organizers.value, ...genres.value, ...rooms.value]
              .map((value) => value.replace('/', '-SLASH-')) // There can be no slashes in the uri
              .join(',')}.json`,
          )
        ).data
      }
      fetchedBanners = await cacheStore.fetch({ key, ttl, fetcher, fallback: [] })
    }

    setBanners(fetchedBanners)
    return fetchedBanners
  }
  async function fetchHighlightedOrganizer() {
    if (organizers.value.length !== 1) {
      setHighlightedOrganizer(undefined)
      return
    }

    const key = `organizer;${locale.value};${organizers.value[0]}`
    const ttl = 3600

    const fetcher = async () => {
      // Non-null: guarded by the `organizers.value.length !== 1` early return above.
      const organizer = await cmsFetch(`/api/${locale.value}/organizer/${encodeURIComponent(organizers.value[0]!)}.json`)
      // We want to ignore organizers that only have no additional information
      return organizer.image || organizer.introduction || organizer.subtitle ? organizer : undefined
    }

    const organizer = await cacheStore.fetch({ key, ttl, fetcher })
    setHighlightedOrganizer(organizer)
    return organizer
  }

  async function fetchAllFacets() {
    const key = `series-overview;${locale.value};all-facets`
    const ttl = 900

    const fetcher = async () => {
      const { elasticSearch } = await graphqlFetch({
        token: 'elasticsearch',
        query: `query allEventFacets($site:[String]) {
            elasticSearch(site:$site, section:"series") {
              facets(limit:1024) {
                genres { label: value }
                organizers: organizer { label: value }
                rooms: room { label: value }
              }
            }
          }`,
        variables: { site: `${locale.value}Default` },
      })
      return elasticSearch.facets
    }

    // Fetch the hits
    const fetchedAllFacets = await cacheStore.fetch({ key, ttl, fetcher })
    setAllFacets(fetchedAllFacets)
    return fetchedAllFacets
  }

  async function fetchSeries() {
    const actualDate = toHour(date.value).toISOString()
    const key = [
      'series-overview',
      locale.value,
      `keyword:${keyword.value}`,
      `date:${actualDate}`,
      `genres:${genres.value.join(',')}`,
      `organizers:${organizers.value.join(',')}`,
      `rooms:${rooms.value.join(',')}`,
    ].join(';')
    const ttl = 300

    const fetcher = async () => {
      // Fetch 1024 results, therefore we will not need to fetch new results when we switch pages
      const { elasticSearch } = await graphqlFetch({
        token: 'elasticsearch',
        query: `query searchEvents($site:[String], $keyword:String, $onlineSaleEnd: [String], $genres:[String], $organizers: [String], $rooms:[String]) {
            elasticSearch(site:$site, section:"series", search:$keyword, genresFacet:$genres, organizerFacet:$organizers, roomFacet:$rooms, importTixOnlineSaleEndRange: $onlineSaleEnd, orderBy: "eventDate") {
              facets(limit:1024) {
                genres { label: value, count }
                organizers: organizer { label: value, count }
                rooms: room { label: value, count }
              }
              count
              hits(limit:1024) {
               uri
              }
            }
          }`,
        variables: {
          site: `${locale.value}Default`,
          onlineSaleEnd: `gte ${actualDate}`, // only show series with OnlineSaleEnd > now
          keyword: keyword.value,
          genres: genres.value,
          organizers: organizers.value,
          rooms: rooms.value,
        },
      })

      return { facets: elasticSearch.facets, hits: elasticSearch.hits.map(({ uri }: { uri: string }) => uri), totalHits: elasticSearch.count }
    }

    // Fetch the hits
    const { facets: fetchedFacets, hits: fetchedHits, totalHits: fetchedTotalHits } = await cacheStore.fetch({ key, ttl, fetcher })
    setFacets(fetchedFacets)
    setHits(fetchedHits)
    setTotalHits(fetchedTotalHits)

    // Initialize the series list with N empty elements, used to trigger loaders
    const slice = fetchedHits.slice((page.value - 1) * hitsPerPage.value, page.value * hitsPerPage.value)
    setSeries(slice.map((uri: string) => ({ loading: true, uri })))

    // Fetch all associated entries within the current page
    const fetchedSeries = await Promise.all(slice.map((uri: string) => pageStore.fetchUri({ uri })))

    // Sort series by amount of events.
    fetchedSeries.sort((a, b) => {
      const firstEventDateA = new Date(a.events[0].eventDate.substring(0, 10))
      const firstEventDateB = new Date(b.events[0].eventDate.substring(0, 10))

      if (firstEventDateA < firstEventDateB) return -1
      if (firstEventDateA > firstEventDateB) return 1

      // If first event dates are the same, sort by the number of events
      return b.events.length - a.events.length
    })

    setSeries(fetchedSeries)
    setLoading(false)
    return fetchedHits
  }

  return {
    allFacets,
    date,
    genres,
    keyword,
    organizers,
    page,
    rooms,
    highlightedOrganizer,
    fallbackBanners,
    banners,
    series,
    rawFacets,
    hits,
    loading,
    totalHits,
    hitsPerPage,
    urlParameters,
    setUrlParameters,
    bannersAtPosition,
    facets,
    activeFacets,
    setAllFacets,
    initialize,
    setHighlightedOrganizer,
    reset,
    setKeyword,
    setPage,
    toggleFacet,
    setFallbackBanners,
    setBanners,
    setFacets,
    setHits,
    setLoading,
    setSeries,
    setTotalHits,
    fetchFallbackBanners,
    fetchBanners,
    fetchHighlightedOrganizer,
    fetchAllFacets,
    fetch: fetchSeries,
  }
})
